import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  MenuItem,
  Menu,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
  Assessment as SummaryIcon,
  Clear as ClearIcon,
  Lock as LockIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { pointageAPI } from '../../../services/api';
import {
  MonthlyPointageResponse,
  PointageStatus,
  POINTAGE_STATUS_CONFIG,
  POINTAGE_STATUS_OPTIONS,
  DAY_NAMES_FR,
  MONTH_NAMES_FR,
} from '../../../types/pointage';
import { extractErrorMessage } from '../../../utils/errorHandler';
import usePdfPreview from '../../../hooks/usePdfPreview';
import useAlert from '../../../hooks/useAlert';
import AlertDialog from '../../../components/AlertDialog';
import TruncatedTextWithTooltip from '../../../components/TruncatedTextWithTooltip';
// pointageExport is dynamically imported in the export handlers so the
// jspdf + exceljs chunks only load when the user clicks Excel/PDF export.

const HR_COLOR = '#00897B';

const PointageGrid: React.FC = () => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [data, setData] = useState<MonthlyPointageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const [nameFilter, setNameFilter] = useState<string>('');

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    employeeId: number;
    date: string;
    currentStatus: PointageStatus | null;
  } | null>(null);

  const { alertState, showError, hideAlert } = useAlert();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const monthlyData = await pointageAPI.getMonthly(month, year);
      setData(monthlyData);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [month, year, showError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const handleNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const handleCellClick = (
    event: React.MouseEvent,
    employeeId: number,
    date: string,
    currentStatus: PointageStatus | null,
    isLocked: boolean
  ) => {
    event.preventDefault();

    // Don't open context menu for locked entries (created from congé)
    if (isLocked) {
      return;
    }

    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      employeeId,
      date,
      currentStatus,
    });
  };

  const handleStatusChange = async (newStatus: PointageStatus) => {
    if (!contextMenu || !data) return;

    const { employeeId, date } = contextMenu;
    setContextMenu(null);

    // Optimistic update - update local state immediately
    setData((prevData) => {
      if (!prevData) return prevData;

      return {
        ...prevData,
        employees: prevData.employees.map((emp) => {
          if (emp.employee_id !== employeeId) return emp;

          const existingEntry = emp.entries[date];
          const updatedEntry = {
            id: existingEntry?.id || 0,
            employee_id: employeeId,
            date: date,
            status: newStatus,
            heure_arrivee: null,
            heure_depart: null,
            heures_travaillees: newStatus.startsWith('PRESENT') ? 191/26 : 0,
            heures_supplementaires: null,
            note: existingEntry?.note || null,
          };

          return {
            ...emp,
            entries: {
              ...emp.entries,
              [date]: updatedEntry,
            },
          };
        }),
      };
    });

    // Call API in background
    try {
      await pointageAPI.toggle({
        employee_id: employeeId,
        date: date,
        status: newStatus,
      });
    } catch (error: any) {
      showError(extractErrorMessage(error));
      // Revert on error - refetch data
      fetchData();
    }
  };

  const handleClearStatus = async () => {
    if (!contextMenu || !data) return;

    const { employeeId, date } = contextMenu;

    // Find the entry ID from the data
    const employee = data.employees.find(e => e.employee_id === employeeId);
    const entry = employee?.entries[date];

    if (entry && entry.id > 0) {
      setContextMenu(null);

      // Optimistic update - remove entry from local state immediately
      setData((prevData) => {
        if (!prevData) return prevData;

        return {
          ...prevData,
          employees: prevData.employees.map((emp) => {
            if (emp.employee_id !== employeeId) return emp;

            const { [date]: removed, ...remainingEntries } = emp.entries;
            return {
              ...emp,
              entries: remainingEntries,
            };
          }),
        };
      });

      // Call API in background
      try {
        await pointageAPI.delete(entry.id);
      } catch (error: any) {
        showError(extractErrorMessage(error));
        // Revert on error - refetch data
        fetchData();
      }
    } else {
      setContextMenu(null);
    }
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Generate days array for the month
  const getDaysInMonth = () => {
    const days = [];
    const daysCount = data?.days_in_month || new Date(year, month, 0).getDate();
    for (let i = 1; i <= daysCount; i++) {
      const d = new Date(year, month - 1, i);
      // Format date as YYYY-MM-DD using local timezone (not UTC)
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push({
        day: i,
        date: dateStr,
        dayOfWeek: d.getDay(),
        isWeekend: d.getDay() === 0, // Sunday only
        isToday: d.toDateString() === today.toDateString(),
      });
    }
    return days;
  };

  const days = getDaysInMonth();

  // Get week spans within the month (Semaine 1, 2, 3, etc.)
  const getWeekSpans = () => {
    const weekSpans: { weekNum: number; colspan: number }[] = [];
    let weekCounter = 1;
    let currentColspan = 0;

    days.forEach((day, index) => {
      currentColspan++;
      // End of week on Sunday or last day of month
      if (day.dayOfWeek === 0 || index === days.length - 1) {
        weekSpans.push({ weekNum: weekCounter, colspan: currentColspan });
        weekCounter++;
        currentColspan = 0;
      }
    });

    return weekSpans;
  };

  const weekSpans = getWeekSpans();

  const renderStatusCell = (
    employeeId: number,
    dateStr: string,
    entry: any,
    isWeekend: boolean,
    isToday: boolean
  ) => {
    // Get the actual status from entry
    let status = entry?.status;

    // Check if entry is locked (created from validated congé)
    const isLocked = entry?.note && entry.note.startsWith('Congé:');

    // For weekend days: show WEEKEND style only if no other status is set
    // This allows employees to have PRESENT or other statuses on weekends
    const hasWorkStatus = status && status !== 'WEEKEND';

    if (!hasWorkStatus && isWeekend) {
      status = 'WEEKEND';
    } else if (status === 'WEEKEND' && !isWeekend) {
      status = null; // Ignore WEEKEND status for non-weekend days
    }

    const config = status ? POINTAGE_STATUS_CONFIG[status as PointageStatus] : null;

    // Determine background color explicitly
    let bgColor = 'transparent'; // Default for working days
    if (isWeekend && !hasWorkStatus) {
      bgColor = '#e0e0e0'; // Darker grey for weekend (Sunday) without work status
    } else if (config?.bgColor && status !== 'WEEKEND') {
      bgColor = config.bgColor;
    }

    const cellContent = (
      <TableCell
        key={dateStr}
        align="center"
        onClick={(e) => handleCellClick(e, employeeId, dateStr, status, isLocked)}
        sx={{
          padding: 0,
          minWidth: 28,
          maxWidth: 28,
          height: 28,
          cursor: isLocked ? 'not-allowed' : 'pointer',
          backgroundColor: bgColor,
          borderTop: '1px solid #bdbdbd',
          borderBottom: '1px solid #bdbdbd',
          borderLeft: 1, borderLeftColor: 'divider',
          borderRight: 1, borderRightColor: 'divider',
          boxShadow: isToday ? 'inset 0 0 0 2px #1976D2' : 'none',
          position: 'relative',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            minHeight: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {config && config.icon && (
            <Typography
              variant="caption"
              sx={{
                color: config.color,
                fontWeight: 600,
                fontSize: '0.65rem',
              }}
            >
              {config.icon}
            </Typography>
          )}
          {isLocked && (
            <LockIcon
              sx={{
                position: 'absolute',
                top: 1,
                right: 1,
                fontSize: '0.5rem',
                color: 'rgba(0,0,0,0.5)',
              }}
            />
          )}
        </div>
      </TableCell>
    );

    // Wrap locked cells with tooltip
    if (isLocked) {
      return (
        <Tooltip
          key={dateStr}
          title={`${entry.note} - Non modifiable`}
          arrow
          placement="top"
        >
          {cellContent}
        </Tooltip>
      );
    }

    return cellContent;
  };

  // Filtered employees based on the name filter (case-insensitive substring match)
  const filteredEmployees = data
    ? nameFilter.trim()
      ? data.employees.filter((emp) => {
          const haystack = `${emp.matricule} ${emp.nom_complet}`.toLowerCase();
          return haystack.includes(nameFilter.trim().toLowerCase());
        })
      : data.employees
    : [];

  // Reusable search input for the Employé header
  const renderEmployeeSearch = () => (
    <TextField
      size="small"
      placeholder="Rechercher employé"
      value={nameFilter}
      onChange={(e) => setNameFilter(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      variant="outlined"
      fullWidth
      InputProps={{
 
        endAdornment: nameFilter ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setNameFilter('')}
              sx={{ p: 0.25, color: 'rgba(255,255,255,0.85)', '&:hover': { color: 'white' } }}
            >
              <ClearIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </InputAdornment>
        ) : null,
        sx: {
          color: 'white',
          fontSize: '0.75rem',
          height: 28,
          bgcolor: 'rgba(255,255,255,0.12)',
          borderRadius: 1,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white', borderWidth: 1.5 },
          '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.18)' },
          '& input::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1, fontStyle: 'italic' },
        },
      }}
    />
  );

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      {/* Month Navigation & Tabs */}
      <Paper sx={{ p: 1, mb: 1, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={handlePrevMonth}>
              <PrevIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ minWidth: 180, textAlign: 'center', fontWeight: 600 }}>
              {MONTH_NAMES_FR[month - 1]} {year}
            </Typography>
            <IconButton size="small" onClick={handleNextMonth}>
              <NextIcon />
            </IconButton>
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                py: 0,
                px: 2,
                fontSize: '0.8rem',
              },
            }}
          >
            <Tab icon={<CalendarIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Pointage" />
            <Tab icon={<SummaryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Récapitulatif" />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {data && data.employees.length > 0 && (
              <>
                <Tooltip title="Exporter Excel">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      const m = await import('../../../utils/pointageExport');
                      activeTab === 0
                        ? m.exportPointageExcel(data, month, year)
                        : m.exportRecapExcel(data, month, year);
                    }}
                    sx={{ color: '#4CAF50' }}
                  >
                    <ExcelIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Exporter PDF">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const isPointage = activeTab === 0;
                      openPreview({
                        getBlob: async () => {
                          const m = await import('../../../utils/pointageExport');
                          return (isPointage
                            ? m.exportPointagePDF(data, month, year, true)
                            : m.exportRecapPDF(data, month, year, true)) as Blob;
                        },
                        fileName: `${isPointage ? 'Pointage' : 'Recapitulatif'}_${month}_${year}.pdf`,
                      });
                    }}
                    sx={{ color: '#F44336' }}
                  >
                    <PdfIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Actualiser">
              <IconButton size="small" onClick={fetchData} sx={{ color: HR_COLOR }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Legend - only show in Pointage tab */}
      {activeTab === 0 && (
        <Paper sx={{ p: 1, mb: 1, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            {Object.entries(POINTAGE_STATUS_CONFIG).map(([key, config]) => (
              <Chip
                key={key}
                label={config.label}
                size="small"
                sx={{
                  backgroundColor: config.bgColor,
                  color: config.color,
                  fontSize: '0.65rem',
                  height: 22,
                }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Tab 0: Pointage Grid */}
      {activeTab === 0 && (
        <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse' }}>
            <TableHead>
              {/* Week Numbers Row */}
              <TableRow>
                <TableCell
                  rowSpan={2}
                  sx={{
                    position: 'sticky',
                    left: 0,
                    top: 0,
                    zIndex: 5,
                    backgroundColor: HR_COLOR,
                    color: 'white',
                    width: 180,
                    minWidth: 180,
                    maxWidth: 180,
                    verticalAlign: 'middle',
                    p: 0.5,
                    borderRight: '2px solid rgba(255,255,255,0.5)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5, py: 0.25, pr: 1.5, pl: 0.5 }}>
                    <Box sx={{ textAlign: 'center', fontWeight: 600, fontSize: '0.85rem' }}>Employé</Box>
                    {renderEmployeeSearch()}
                  </Box>
                </TableCell>
                {/* <TableCell
                  rowSpan={2}
                  sx={{
                    position: 'sticky',
                    left: 180,
                    top: 0,
                    zIndex: 5,
                    backgroundColor: HR_COLOR,
                    color: 'white',
                    width: 55,
                    minWidth: 55,
                    maxWidth: 55,
                    verticalAlign: 'middle',
                    textAlign: 'center',
                    p: 0.5,
                  }}
                >
                  Statut
                </TableCell> */}
                {weekSpans.map((week) => (
                  <TableCell
                    key={`week-${week.weekNum}`}
                    colSpan={week.colspan}
                    align="center"
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      backgroundColor: '#00695C',
                      color: 'white',
                      padding: '4px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      borderLeft: '1px solid rgba(255,255,255,0.3)',
                    }}
                  >
                    Semaine {week.weekNum}
                  </TableCell>
                ))}
              </TableRow>
            {/* Days Row */}
            <TableRow>
              {days.map((day) => (
                <TableCell
                  key={day.date}
                  align="center"
                  sx={{
                    position: 'sticky',
                    top: 33,
                    zIndex: 2,
                    backgroundColor: day.isWeekend ? '#e0e0e0' : day.isToday ? '#bbdefb' : HR_COLOR,
                    color: day.isWeekend ? 'text.secondary' : 'white',
                    padding: '2px',
                    minWidth: 28,
                    maxWidth: 28,
                    fontSize: '0.6rem',
                    height: 50,
                    borderBottom: '2px solid #bdbdbd',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <span style={{
                      writingMode: 'vertical-rl',
                      textOrientation: 'mixed',
                      transform: 'rotate(180deg)',
                      fontSize: '0.55rem',
                    }}>
                      {DAY_NAMES_FR[day.dayOfWeek]}
                    </span>
                    <span style={{ fontWeight: 600, marginTop: 2 }}>{day.day}</span>
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={days.length + 1} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={40} sx={{ color: HR_COLOR }} />
                </TableCell>
              </TableRow>
            ) : !data || filteredEmployees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={days.length + 1} align="center" sx={{ py: 4 }}>
                  Aucune donnée.
                </TableCell>
              </TableRow>
            ) : (
              filteredEmployees.map((emp) => (
                <TableRow key={emp.employee_id} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      backgroundColor: 'background.paper',
                      borderRight: '2px solid',
                      borderRightColor: 'divider',
                      borderBottom: '1px solid',
                      borderBottomColor: 'divider',
                      width: 180,
                      minWidth: 180,
                      maxWidth: 180,
                      p: 0.5,
                    }}
                  >
                    <TruncatedTextWithTooltip
                      text={`${emp.matricule} - ${emp.nom_complet}`}
                      maxWidth={170}
                    />
                  </TableCell>
                  {/* <TableCell
                    align="center"
                    sx={{
                      position: 'sticky',
                      left: 180,
                      zIndex: 1,
                      backgroundColor: 'background.paper',
                      borderRight: 1, borderRightColor: 'divider',
                      width: 55,
                      minWidth: 55,
                      maxWidth: 55,
                      p: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#fff',
                        backgroundColor: emp.statut === 'ACTIF' ? '#4CAF50' :
                               emp.statut === 'INACTIF' ? '#F44336' :
                               emp.statut === 'SUSPENDU' ? '#FF9800' : '#9E9E9E',
                        fontWeight: 600,
                        px: 0.5,
                        py: 0.2,
                        borderRadius: 0.5,
                        fontSize: '0.65rem',
                      }}
                    >
                      {emp.statut || '-'}
                    </Typography>
                  </TableCell> */}
                  {days.map((day) => {
                    const entry = emp.entries[day.date];
                    return renderStatusCell(
                      emp.employee_id,
                      day.date,
                      entry,
                      day.isWeekend,
                      day.isToday
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Tab 1: Récapitulatif */}
      {activeTab === 1 && (
        <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  rowSpan={2}
                  sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 5,
                    backgroundColor: HR_COLOR,
                    color: 'white',
                    width: 180,
                    minWidth: 180,
                    maxWidth: 180,
                    fontWeight: 600,
                    p: 0.5,
                    borderRight: '2px solid rgba(255,255,255,0.5)',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0.5, py: 0.25, pr: 1.5, pl: 0.5 }}>
                    <Box sx={{ textAlign: 'center', fontWeight: 600, fontSize: '0.85rem' }}>Employé</Box>
                    {renderEmployeeSearch()}
                  </Box>
                </TableCell>
                {/* <TableCell
                  rowSpan={2}
                  sx={{
                    position: 'sticky',
                    left: 180,
                    zIndex: 5,
                    backgroundColor: HR_COLOR,
                    color: 'white',
                    width: 55,
                    minWidth: 55,
                    maxWidth: 55,
                    fontWeight: 600,
                    textAlign: 'center',
                    p: 0.5,
                  }}
                >
                  Statut
                </TableCell> */}
                {/* Présences */}
                <TableCell align="center" colSpan={2} sx={{ backgroundColor: '#E91E63', color: 'white', fontWeight: 600 }}>
                  Présences
                </TableCell>
                {/* Absences */}
                <TableCell align="center" colSpan={1} sx={{ backgroundColor: '#9C27B0', color: 'white', fontWeight: 600 }}>
                  Absences
                </TableCell>
                {/* Congés */}
                <TableCell align="center" colSpan={7} sx={{ backgroundColor: '#FF9800', color: 'white', fontWeight: 600 }}>
                  Congés
                </TableCell>
              </TableRow>
              <TableRow>
                {/* Présences sub-headers */}
                <TableCell align="center" sx={{ backgroundColor: '#EC407A', color: 'white', fontSize: '0.75rem', fontWeight: 600, minWidth: 60 }}>
                  J. Prés.
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#EC407A', color: 'white', fontSize: '0.75rem', fontWeight: 600, minWidth: 60 }}>
                  H. Prés.
                </TableCell>
                {/* Absences sub-headers */}
                <TableCell align="center" sx={{ backgroundColor: '#AB47BC', color: 'white', fontSize: '0.75rem', fontWeight: 600, minWidth: 50 }}>
                  Abs.
                </TableCell>
                {/* Congés sub-headers */}
                <TableCell align="center" sx={{ backgroundColor: '#FFA726', color: 'white', fontSize: '0.75rem', fontWeight: 600, minWidth: 60 }}>
                  C. Payé
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#AB47BC', color: 'white', fontSize: '0.75rem', fontWeight: 600, minWidth: 60 }}>
                  Maladie
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#FFA726', color: 'white', fontSize: '0.75rem', fontWeight: 600, minWidth: 60 }}>
                  Mariage
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#FFA726', color: 'white', fontSize: '0.75rem', fontWeight: 600, minWidth: 50 }}>
                  Décès
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#FFA726', color: 'white', fontSize: '0.75rem', fontWeight: 600, minWidth: 60 }}>
                  Naiss.
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#607D8B', color: 'white', fontSize: '0.75rem', fontWeight: 600, minWidth: 50 }}>
                  Férié
                </TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#FFA726', color: 'white', fontSize: '0.75rem', fontWeight: 600, minWidth: 70 }}>
                  Tot. Cong.
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} sx={{ color: HR_COLOR }} />
                  </TableCell>
                </TableRow>
              ) : !data || filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    Aucune donnée disponible.
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((emp) => (
                  <TableRow key={emp.employee_id}>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        backgroundColor: 'background.paper',
                        borderRight: '2px solid',
                        borderRightColor: 'divider',
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        width: 180,
                        minWidth: 180,
                        maxWidth: 180,
                        p: 0.5,
                      }}
                    >
                      <TruncatedTextWithTooltip
                        text={`${emp.matricule} - ${emp.nom_complet}`}
                        maxWidth={170}
                      />
                    </TableCell>
                    {/* <TableCell
                      align="center"
                      sx={{
                        position: 'sticky',
                        left: 180,
                        zIndex: 1,
                        backgroundColor: 'background.paper',
                        borderRight: 1, borderRightColor: 'divider',
                        width: 55,
                        minWidth: 55,
                        maxWidth: 55,
                        p: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#fff',
                          backgroundColor: emp.statut === 'ACTIF' ? '#4CAF50' :
                                 emp.statut === 'INACTIF' ? '#F44336' :
                                 emp.statut === 'SUSPENDU' ? '#FF9800' : '#9E9E9E',
                          fontWeight: 600,
                          px: 0.5,
                          py: 0.2,
                          borderRadius: 0.5,
                          fontSize: '0.65rem',
                        }}
                      >
                        {emp.statut || '-'}
                      </Typography>
                    </TableCell> */}
                    {/* Présences */}
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#E91E63', fontSize: '0.85rem' }}>
                      {emp.total_jours_present}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#E91E63', fontSize: '0.85rem' }}>
                      {emp.total_heures_present}
                    </TableCell>
                    {/* Absences */}
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#9C27B0', fontSize: '0.85rem' }}>
                      {emp.total_absent}
                    </TableCell>
                    {/* Congés */}
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#FF9800', fontSize: '0.85rem' }}>
                      {emp.total_conge_paye}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#9C27B0', fontSize: '0.85rem' }}>
                      {emp.total_conge_maladie}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#FF9800', fontSize: '0.85rem' }}>
                      {emp.total_conge_mariage}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#FF9800', fontSize: '0.85rem' }}>
                      {emp.total_conge_deces}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#FF9800', fontSize: '0.85rem' }}>
                      {emp.total_conge_naissance}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#607D8B', fontSize: '0.85rem' }}>
                      {emp.total_jour_ferie}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: '#FF9800', fontSize: '0.85rem' }}>
                      {emp.total_conge}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Context Menu for Status Selection */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {POINTAGE_STATUS_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleStatusChange(option.value)}
            selected={contextMenu?.currentStatus === option.value}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: POINTAGE_STATUS_CONFIG[option.value].bgColor,
              }}
            />
            {option.label}
          </MenuItem>
        ))}
        {contextMenu?.currentStatus && (
          <>
            <Divider />
            <MenuItem
              onClick={handleClearStatus}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: '#f44336',
              }}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
              Vider
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        showCancel={alertState.showCancel}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default PointageGrid;
